import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { PageLoader } from './components/ui/PageLoader';
import { ToastProvider } from './components/ui/ToastProvider';
import { useAppStore } from './stores/app-store';
import { useProfessionalStore } from './stores/professional-store';
import { useCmsModulesPreload } from './hooks/useCmsModulesPreload';

// Shepherd.js styles
import 'shepherd.js/dist/css/shepherd.css';
import './styles/shepherd.css';

// Lazy load all pages for code splitting
const ProfileSelection = lazy(() =>
  import('./pages/ProfileSelection').then((m) => ({ default: m.ProfileSelection }))
);
const NotesHistory = lazy(() =>
  import('./pages/NotesHistory').then((m) => ({ default: m.NotesHistory }))
);
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));

// CMS Admin
const ContentManager = lazy(() =>
  import('./pages/admin/ContentManager').then((m) => ({ default: m.ContentManager }))
);

// Lazy load heavy components
const ProfileAppLayout = lazy(() =>
  import('./components/layout/ProfileAppLayout').then((m) => ({ default: m.ProfileAppLayout }))
);
const ModularNoteForm = lazy(() =>
  import('./components/ModularNoteForm').then((m) => ({ default: m.ModularNoteForm }))
);

function App() {
  // Précharger les modules CMS au démarrage
  const { isLoading: modulesLoading } = useCmsModulesPreload();

  // Utiliser le store persisté pour la sélection de modules
  const { selectedModules, resetModuleSelection } = useAppStore();

  // Vérifier si l'utilisateur a déjà sélectionné son profil professionnel
  const { hasSelectedProfile } = useProfessionalStore();

  // Afficher loader pendant le préchargement des modules
  if (modulesLoading) {
    return <PageLoader />;
  }

  return (
    <ToastProvider>
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Page de sélection du profil professionnel */}
            <Route path="/profile-selection" element={<ProfileSelection />} />

            {/* Redirect root - vers sélection de profil si pas encore fait */}
            <Route
              path="/"
              element={
                hasSelectedProfile ? (
                  <Navigate to="/app" replace />
                ) : (
                  <Navigate to="/profile-selection" replace />
                )
              }
            />

            {/* Main app routes - Hybrid Profile workflow */}
            <Route
              path="/app"
              element={
                <ProfileAppLayout onStartNote={() => {}} onReset={resetModuleSelection}>
                  <ModularNoteForm selectedModules={selectedModules} onBack={() => {}} />
                </ProfileAppLayout>
              }
            />

            <Route path="/app/history" element={<NotesHistory />} />

            <Route path="/app/settings" element={<Settings />} />

            {/* CMS Admin - Révision clinique */}
            <Route path="/admin/cms" element={<ContentManager />} />

            {/* Catch all - redirect to app */}
            <Route path="*" element={<Navigate to="/app" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </ToastProvider>
  );
}

export default App;
